import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';

import { Txt } from '@app/components/common';

const CX = 400;
const LINE = '#94A3B8';
const FONT = 'system-ui, -apple-system, Segoe UI, sans-serif';

const PROCESS = { fill: '#EAF3FF', stroke: '#7EB6FF', color: '#1E4B8A' };
const ERROR = { fill: '#FDE8E8', stroke: '#F09595', color: '#B42318' };
const SKIP = { fill: '#F3F4F6', stroke: '#C5CAD3', color: '#4B5563' };
const OK = { fill: '#E5F8EC', stroke: '#6FCF97', color: '#166534' };
const DIAMOND = { fill: '#FFE8C2', stroke: '#F5A524', color: '#9A5412' };

type Tone = { fill: string; stroke: string; color: string };

const FlowBox = ({
  x,
  y,
  w,
  h,
  lines,
  hint,
  tone,
  rx = 8,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  lines: string[];
  hint?: string;
  tone: Tone;
  rx?: number;
}) => {
  const content = hint ? [...lines, hint] : lines;
  const lineHeight = 16;
  const start = y + h / 2 - ((content.length - 1) * lineHeight) / 2;

  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={rx} fill={tone.fill} stroke={tone.stroke} strokeWidth="1.5" />
      <text textAnchor="middle" fontFamily={FONT} fill={tone.color}>
        {content.map((line, index) => (
          <tspan
            key={line}
            x={x + w / 2}
            y={start + index * lineHeight}
            fontSize={hint && index === content.length - 1 ? 11 : 13}
            fontWeight={hint && index === content.length - 1 ? 500 : 600}
            dominantBaseline="middle"
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const FlowDiamond = ({
  cx,
  cy,
  w,
  h,
  lines,
  hint,
}: {
  cx: number;
  cy: number;
  w: number;
  h: number;
  lines: string[];
  hint?: string;
}) => {
  const content = hint ? [...lines, hint] : lines;
  const lineHeight = 15;
  const start = cy - ((content.length - 1) * lineHeight) / 2;
  const points = `${cx},${cy - h / 2} ${cx + w / 2},${cy} ${cx},${cy + h / 2} ${cx - w / 2},${cy}`;

  return (
    <g>
      <polygon points={points} fill={DIAMOND.fill} stroke={DIAMOND.stroke} strokeWidth="1.5" />
      <text textAnchor="middle" fontFamily={FONT} fill={DIAMOND.color}>
        {content.map((line, index) => (
          <tspan
            key={line}
            x={cx}
            y={start + index * lineHeight}
            fontSize={hint && index === content.length - 1 ? 11 : 13}
            fontWeight={hint && index === content.length - 1 ? 500 : 600}
            dominantBaseline="middle"
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const Arrow = ({ d }: { d: string }) => (
  <path d={d} fill="none" stroke={LINE} strokeWidth="1.5" markerEnd="url(#outlet-routes-arrow)" />
);

const Wire = ({ d }: { d: string }) => (
  <path d={d} fill="none" stroke={LINE} strokeWidth="1.5" />
);

const EdgeLabel = ({ x, y, children }: { x: number; y: number; children: string }) => (
  <text x={x} y={y} textAnchor="middle" fontFamily={FONT} fontSize="11" fill="#64748B" dominantBaseline="auto">
    {children}
  </text>
);

const BranchLabel = ({ x, y, children }: { x: number; y: number; children: string }) => (
  <text x={x} y={y} textAnchor="middle" fontFamily={FONT} fontSize="13" fontWeight="700" fill="#374151">
    {children}
  </text>
);

const cellBorder = {
  border: '1px solid',
  borderColor: 'blue.200',
};

export const OutletRoutesImportScheme = () => (
  <Box
    borderWidth="1px"
    borderColor="blue.100"
    borderRadius="xl"
    bg="white"
    p={{ base: 3, md: 6 }}
  >
    <Txt textAlign="center" fontSize="xl" fontWeight="bold" color="blue.900">
      Импорт маршрутов: схема обработки одной строки
    </Txt>
    <Txt textAlign="center" fontSize="sm" color="gray.600" mb={4}>
      ID точки · название маршрута · дни посещения
    </Txt>

    <svg
      viewBox="0 0 1020 990"
      role="img"
      aria-labelledby="outlet-routes-scheme-title"
      width="100%"
      height="auto"
      style={{ display: 'block' }}
    >
      <title id="outlet-routes-scheme-title">Схема обработки одной строки импорта маршрутов</title>
      <defs>
        <marker
          id="outlet-routes-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={LINE} />
        </marker>
      </defs>

      <FlowBox x={290} y={8} w={220} h={42} rx={21} lines={['Строка CSV / XLSX']} tone={PROCESS} />
      <Arrow d={`M ${CX} 50 V 70`} />

      <FlowBox
        x={250}
        y={70}
        w={300}
        h={56}
        lines={['Проверить ID точки', 'и название маршрута']}
        tone={PROCESS}
      />
      <EdgeLabel x={655} y={90}>Некорректные данные</EdgeLabel>
      <Arrow d="M 550 98 H 760" />
      <FlowBox x={760} y={78} w={200} h={40} lines={['Ошибка строки']} tone={ERROR} />

      <Arrow d={`M ${CX} 126 V 150`} />

      <FlowBox
        x={250}
        y={150}
        w={300}
        h={56}
        lines={['Найти точку и маршрут', 'с точным названием']}
        tone={PROCESS}
      />
      <Wire d="M 550 178 H 580" />
      <Wire d="M 580 150 V 214" />
      <EdgeLabel x={705} y={142}>Несколько точных совпадений</EdgeLabel>
      <Arrow d="M 580 150 H 820" />
      <FlowBox x={820} y={130} w={180} h={40} lines={['Ошибка строки']} tone={ERROR} />
      <EdgeLabel x={705} y={206}>Точка или маршрут не найдены</EdgeLabel>
      <Arrow d="M 580 214 H 820" />
      <FlowBox x={820} y={194} w={180} h={40} lines={['Пропуск строки']} tone={SKIP} />

      <Arrow d={`M ${CX} 206 V 240`} />

      <FlowBox
        x={250}
        y={240}
        w={300}
        h={62}
        lines={['Загрузить все привязки точки']}
        hint="Активные и неактивные"
        tone={PROCESS}
      />
      <Arrow d={`M ${CX} 302 V 322`} />

      <FlowDiamond
        cx={CX}
        cy={384}
        w={320}
        h={124}
        lines={['Деактивировать', 'старые привязки?']}
        hint="deactivate_existing"
      />
      <Wire d={`M ${CX} 446 V 466`} />
      <Wire d="M 170 466 H 630" />
      <BranchLabel x={198} y={458}>Да</BranchLabel>
      <BranchLabel x={602} y={458}>Нет</BranchLabel>
      <Arrow d="M 170 466 V 482" />
      <FlowBox
        x={30}
        y={482}
        w={280}
        h={72}
        lines={['Деактивировать все', 'активные привязки']}
        tone={PROCESS}
      />
      <Wire d="M 170 554 V 580" />
      <Wire d="M 630 466 V 580" />
      <Wire d="M 170 580 H 630" />
      <Arrow d={`M ${CX} 580 V 604`} />

      <FlowDiamond
        cx={CX}
        cy={672}
        w={420}
        h={136}
        lines={['Есть неактивная привязка', 'с тем же route_number_id?']}
      />
      <Wire d={`M ${CX} 740 V 760`} />
      <Wire d="M 160 760 H 660" />
      <BranchLabel x={188} y={752}>Да</BranchLabel>
      <BranchLabel x={632} y={752}>Нет</BranchLabel>
      <Arrow d="M 160 760 V 776" />
      <Arrow d="M 660 760 V 776" />
      <FlowBox
        x={25}
        y={776}
        w={270}
        h={72}
        lines={['Восстановить существующую']}
        hint="PATCH · is_active = true"
        tone={OK}
      />
      <FlowBox
        x={525}
        y={776}
        w={270}
        h={72}
        lines={['Создать новую привязку']}
        hint="POST"
        tone={PROCESS}
      />
      <Wire d="M 160 848 V 874" />
      <Wire d="M 660 848 V 874" />
      <Wire d="M 160 874 H 660" />
      <Arrow d={`M ${CX} 874 V 894`} />

      <FlowBox
        x={230}
        y={894}
        w={340}
        h={72}
        rx={36}
        lines={['Запись обработана']}
        hint="Сохранить результат → следующая строка."
        tone={OK}
      />
    </svg>

    <Box mt={6}>
      <Txt textAlign="center" fontWeight="bold" color="blue.900" mb={2}>
        Как заполняется день визита
      </Txt>
      <Table size="sm" variant="unstyled">
        <Thead>
          <Tr>
            <Th {...cellBorder} textAlign="center" textTransform="none" letterSpacing="normal" color="blue.900" bg="blue.50">Значение в ячейке</Th>
            <Th {...cellBorder} textAlign="center" textTransform="none" letterSpacing="normal" color="blue.900" bg="blue.50">Повторная активация</Th>
            <Th {...cellBorder} textAlign="center" textTransform="none" letterSpacing="normal" color="blue.900" bg="blue.50">Создание</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td {...cellBorder}>Пустая ячейка</Td>
            <Td {...cellBorder} bg="green.100">Оставить без изменений</Td>
            <Td {...cellBorder} bg="yellow.100">Пустая строка: &quot;&quot;</Td>
          </Tr>
          <Tr>
            <Td {...cellBorder}>не посещается</Td>
            <Td {...cellBorder} bg="yellow.100">Пустая строка: &quot;&quot;</Td>
            <Td {...cellBorder} bg="yellow.100">Пустая строка: &quot;&quot;</Td>
          </Tr>
          <Tr>
            <Td {...cellBorder}>Указаны дни</Td>
            <Td {...cellBorder}>Записать дни из ячейки</Td>
            <Td {...cellBorder}>Записать дни из ячейки</Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>

    <Box mt={4} borderWidth="1px" borderColor="blue.200" borderRadius="lg" bg="blue.50" p={4}>
      <Txt fontWeight="bold" color="blue.900" mb={2}>Примечания:</Txt>
      <Box as="ol" pl={5} color="gray.700" fontSize="sm" sx={{ '& li + li': { mt: 1 } }}>
        <Box as="li">
          Ошибка API → ошибка строки. После сбоя деактивации или восстановления новая связь не создаётся.
        </Box>
      </Box>
    </Box>
  </Box>
);
